import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import logo from "../../assets/UpdatedMainLogo.png";
import getApiBase from "../../components/getApiBase";
import ConfidenceScoreDetails from "../components/ConfidenceScoreDetails";
import ConfidenceScore from "../components/ConfidenceScore";

// Header with real Autoshops.com logo
const Header = ({ onLogout, showLogout = false }) => {
  const navigate = useNavigate();
  
  return (
    <header style={{
      backgroundColor: '#fff',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      padding: '10px 20px',
      position: 'sticky',
      top: 0,
      zIndex: 1000
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <img src={logo} alt="Autoshops.com Logo" style={{ height: 'auto', width: '300px' }} />
        </Box>
        <nav style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {showLogout && (
            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate('/admin');
              }}
              style={{ 
                color: '#ea4335', 
                textDecoration: 'none', 
                fontFamily: 'Poppins, sans-serif', 
                fontWeight: 700,
                fontSize: '18px',
                padding: '10px',
                borderRadius: '8px',
                transition: 'background-color 0.2s ease, color 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#e63926';
                e.target.style.color = '#fff';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#ea4335';
              }}
            >
              ← Back to Dashboard
            </a>
          )}
          <span style={{ 
            color: '#000', 
            fontFamily: 'Poppins, sans-serif', 
            fontWeight: 700, 
            fontSize: '18px',
            padding: '10px' 
          }}>
            Shop Management
          </span>
          {showLogout && onLogout && (
            <Button
              onClick={onLogout}
              variant="contained"
              sx={{
                backgroundColor: '#4285f4',
                color: 'white',
                borderRadius: '20px',
                padding: '8px 16px',
                fontSize: '16px',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#1b449e',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Logout
            </Button>
          )}
        </nav>
      </Box>
    </header>
  );
};

const AdminDashboard = () => {
  const [searchParams] = useSearchParams();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Read status from URL query param, default to 'Pending'
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'Pending');
  const [tierFilter, setTierFilter] = useState('All'); // Default to All tiers
  const [zipFilter, setZipFilter] = useState(''); // ZIP code filter
  const [showApprovalGuide, setShowApprovalGuide] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedShopForReject, setSelectedShopForReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [nameFilter, setNameFilter] = useState('');
  const [selectedShopForConfidence, setSelectedShopForConfidence] = useState(null);
  const [myPermissions, setMyPermissions] = useState(null);
  const navigate = useNavigate();

  // Format phone number to (XXX) XXX-XXXX format
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';
    
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Check if we have a valid US phone number (10 digits)
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    // Check if we have 11 digits (with US/Canada country code 1)
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    
    // Handle other country codes (12+ digits or 11 digits not starting with 1)
    if (cleaned.length >= 11) {
      // For international numbers, show country code + formatted number
      if (cleaned.length === 11 && cleaned[0] !== '1') {
        // 11 digits, not US - likely another country code
        return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
      } else if (cleaned.length === 12) {
        // 12 digits - likely 2-digit country code
        return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8)}`;
      } else if (cleaned.length === 13) {
        // 13 digits - likely 3-digit country code
        return `+${cleaned.slice(0, 3)} (${cleaned.slice(3, 6)}) ${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
      } else {
        // For very long numbers, just show with country code separated
        const countryCodeLength = cleaned.length - 10;
        const countryCode = cleaned.slice(0, countryCodeLength);
        const number = cleaned.slice(countryCodeLength);
        return `+${countryCode} (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
      }
    }
    
    // If it's not a standard format, return as-is but indicate it might be incomplete
    return phoneNumber + (cleaned.length < 10 ? ' (incomplete)' : '');
  };

  const forceRefresh = () => {
    setRefreshKey(prev => prev + 1);
    setShops([]);
    setError('');
    setLoading(true);
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint (consistent with main app)
      await fetch(`${getApiBase()}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      
      // No localStorage cleanup needed - using cookies
      
      // Redirect to login page
      
      localStorage.removeItem("user");
      navigate('/login');
    } catch (err) {
      console.error("❌ Logout failed:", err);
      // Still redirect even if API call fails
      navigate('/login');
    }
  };

  const handleShowConfidenceDetails = (shop) => {
    setSelectedShopForConfidence(shop);
  };

  // Fetch shops when component mounts or filter changes
  React.useEffect(() => {
    // Clear existing data to show loading state
    setShops([]);
    setError('');
    fetchShops();
  }, [statusFilter, tierFilter, zipFilter, refreshKey]);

  // Fetch permissions on mount
  React.useEffect(() => {
    fetchMyPermissions();
  }, []);

  // Define functions after effects to avoid hoisting issues
  async function fetchMyPermissions() {
    try {
      const response = await fetch(`${getApiBase()}/api/admin-management/permissions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Permissions received:', data);
        console.log('Full permissions object:', JSON.stringify(data, null, 2));
        setMyPermissions(data);
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  }

  async function fetchShops() {
    try {
      setLoading(true);
      
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const randomId = Math.random().toString(36).substring(7);
      
      // Build query params based on filters
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'All') {
        params.append('status', statusFilter);
      }
      if (tierFilter && tierFilter !== 'All') {
        params.append('tier', tierFilter);
      }
      if (zipFilter && zipFilter.trim() !== '') {
        params.append('zip', zipFilter.trim());
      }
      params.append('_t', timestamp);
      params.append('_r', randomId);
      
      const endpoint = `${getApiBase()}/api/admin/all-shops?${params.toString()}`;
        
      console.log(`🔄 Making API call to: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
      });

      console.log(`📡 Response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Fetched shops (${data.length} total):`, data);
        // Ensure we always have an array
        setShops(Array.isArray(data) ? data : []);
      } else {
        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          setError('Session expired. Please log in again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }
        
        const errorData = await response.json();
        setError(errorData.error || `Failed to fetch shops`);
        console.error('❌ Failed to fetch shops:', errorData);
        setShops([]); // Ensure shops is always an array
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('❌ Network error:', err);
      setShops([]); // Ensure shops is always an array
    } finally {
      setLoading(false);
    }
  }

  // Generate auto-suggested rejection reason based on shop data
  const generateRejectionReason = (shop) => {
    const issues = [];
    
    if (shop.confidence < 50) {
      issues.push(`Confidence score too low (${shop.confidence}% - need 50%+)`);
    }
    
    if (!shop.business_license_url) {
      issues.push('Business license not uploaded');
    }
    
    if (!shop.storefront_photo_url) {
      issues.push('Storefront photo not uploaded');
    }
    
    if (issues.length > 0) {
      return `Your shop application has been rejected for the following reasons:\n\n${issues.map((issue, i) => `${i + 1}. ${issue}`).join('\n')}\n\nPlease address these issues and contact support to request reactivation.`;
    }
    
    return 'Your shop application has been rejected. Please contact support for more information.';
  };

  const handleRejectClick = (shop) => {
    setSelectedShopForReject(shop);
    setRejectionReason(generateRejectionReason(shop));
    setRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason.');
      return;
    }

    try {
      const response = await fetch(`${getApiBase()}/api/admin/approve-shop/${selectedShopForReject.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          status: 'Suspended',
          rejection_reason: rejectionReason 
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Shop rejected:', result);
        setRejectModalOpen(false);
        setSelectedShopForReject(null);
        setRejectionReason('');
        fetchShops();
      } else {
        const errorData = await response.json();
        console.error('❌ Rejection failed:', errorData);
        alert('Failed to reject shop: ' + (errorData.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('❌ Rejection error:', err);
      alert('Network error during rejection. Please try again.');
    }
  };

  const handleStatusChange = async (shopId, newStatus) => {
    try {
      const response = await fetch(`${getApiBase()}/api/admin/approve-shop/${shopId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Shop status updated:', result);
        // Refresh the shops list
        fetchShops();
      } else {
        const errorData = await response.json();
        console.error('❌ Status update failed:', errorData);
        
        if (response.status === 401) {
          alert('Authentication failed. Please log in again through the main app.');
          window.location.href = '/login';
        } else {
          alert('Failed to update shop status: ' + (errorData.error || 'Unknown error'));
        }
      }
    } catch (err) {
      console.error('❌ Status update error:', err);
      alert('Network error during status update. Please try again.');
    }
  };

  const handleTierChange = async (shopId, newTier) => {
    try {
      const response = await fetch(`${getApiBase()}/api/admin/approve-shop/${shopId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ saas_model: newTier }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Shop tier updated:', result);
        // Refresh the shops list
        fetchShops();
      } else {
        const errorData = await response.json();
        console.error('❌ Tier update failed:', errorData);
        
        if (response.status === 401) {
          alert('Authentication failed. Please log in again through the main app.');
          window.location.href = '/login';
        } else {
          alert('Failed to update shop tier: ' + (errorData.error || 'Unknown error'));
        }
      }
    } catch (err) {
      console.error('❌ Tier update error:', err);
      alert('Network error during tier update. Please try again.');
    }
  };

  return (
    <>
      <Header onLogout={handleLogout} showLogout={true} />
      <Box sx={{ 
        fontFamily: 'Poppins, sans-serif', 
        padding: '20px',
        backgroundColor: '#f8fafc',
        minHeight: '100vh'
      }}>
        <Typography variant="h4" sx={{ color: '#333', marginBottom: '20px' }}>Shop Registration Management</Typography>
        
        <Box sx={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
          <Typography variant="h5" sx={{ color: '#e63926', margin: '0 0 15px 0' }}>Shop Management Dashboard</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap', flex: '1' }}>
              <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label style={{ fontWeight: 600, color: '#374151' }}>Filter by Status:</label>
                <select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="Pending">Pending Review</option>
                  <option value="Active">Active</option>
                  <option value="NIN">Not In Network</option>
                  <option value="Inactive">Inactive</option>
                  <option value="All">All Statuses</option>
                </select>
              </Box>
              
              <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label style={{ fontWeight: 600, color: '#374151' }}>Filter by Tier:</label>
                <select 
                  value={tierFilter} 
                  onChange={(e) => setTierFilter(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="All">All Tiers</option>
                  <option value="Free">Free</option>
                  <option value="Pro">Pro</option>
                  <option value="Elite">Elite</option>
                </select>
              </Box>
              
              <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label style={{ fontWeight: 600, color: '#374151' }}>Filter by ZIP:</label>
                <input
                  type="text"
                  placeholder="Enter ZIP code..."
                  value={zipFilter}
                  onChange={(e) => {
                    // Only allow digits, max 5 characters
                    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setZipFilter(value);
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '0.875rem',
                    width: '120px'
                  }}
                />
                {zipFilter && (
                  <Button
                    onClick={() => setZipFilter('')}
                    size="small"
                    sx={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      fontSize: '12px',
                      fontFamily: 'Poppins, sans-serif',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#dc2626'
                      }
                    }}
                  >
                    ✕ Clear
                  </Button>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label style={{ fontWeight: 600, color: '#374151' }}>Search by Name:</label>
                <input
                  type="text"
                  placeholder="Enter shop name..."
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '0.875rem',
                    minWidth: '200px'
                  }}
                />
                {nameFilter && (
                  <Button
                    onClick={() => setNameFilter('')}
                    size="small"
                    sx={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      fontSize: '12px',
                      fontFamily: 'Poppins, sans-serif',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#dc2626'
                      }
                    }}
                  >
                    ✕ Clear
                  </Button>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                onClick={() => setShowApprovalGuide(!showApprovalGuide)}
                variant="contained"
                sx={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#4b5563'
                  }
                }}
              >
                {showApprovalGuide ? 'Hide' : 'Show'} Approval Guide
              </Button>
              <Button
                onClick={forceRefresh}
                variant="contained"
                sx={{
                  backgroundColor: '#10b981',
                  color: 'white',
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#059669'
                  }
                }}
              >
                🔄 Force Refresh
              </Button>
              {myPermissions?.permissions?.manage_admins && (
                <Button
                  onClick={() => navigate('/admin/management')}
                  variant="contained"
                  sx={{
                    backgroundColor: '#8b5cf6',
                    color: 'white',
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#7c3aed'
                    }
                  }}
                >
                  👥 Manage Admins
                </Button>
              )}
            </Box>
          </Box>

          {console.log('Manage Admins button check:', myPermissions?.permissions?.manage_admins)}
          {console.log('Full permissions object:', myPermissions)}

          {showApprovalGuide && (
            <Box sx={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <Typography variant="h6" sx={{ color: '#0369a1', marginTop: 0, marginBottom: '12px' }}>🎯 Approval Guidelines</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '0.875rem' }}>
                <div>
                  <strong style={{ color: '#059669' }}>🟢 ACTIVE:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#374151' }}>
                    <li>Verified shops</li>
                    <li>Ready for customers</li>
                    <li>Can receive jobs</li>
                  </ul>
                </div>
                <div>
                  <strong style={{ color: '#f59e0b' }}>⏳ PENDING:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#374151' }}>
                    <li>New registrations</li>
                    <li>Awaiting verification</li>
                    <li>Review confidence score</li>
                  </ul>
                </div>
                <div>
                  <strong style={{ color: '#6b7280' }}>🚫 NIN:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#374151' }}>
                    <li>Google imports</li>
                    <li>Not registered</li>
                    <li>No owner claimed</li>
                  </ul>
                </div>
                <div>
                  <strong style={{ color: '#dc2626' }}>❌ INACTIVE:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#374151' }}>
                    <li>Suspended shops</li>
                    <li>Policy violations</li>
                    <li>Quality issues</li>
                  </ul>
                </div>
              </Box>
              <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e0e7ff' }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '0.875rem' }}>
                <div>
                  <strong style={{ color: '#10b981' }}>🆓 FREE TIER:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#374151' }}>
                    <li>Basic listing</li>
                    <li>Default tier</li>
                    <li>Limited features</li>
                  </ul>
                </div>
                <div>
                  <strong style={{ color: '#3b82f6' }}>💎 PRO TIER:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#374151' }}>
                    <li>Enhanced visibility</li>
                    <li>Priority search</li>
                    <li>More features</li>
                  </ul>
                </div>
                <div>
                  <strong style={{ color: '#8b5cf6' }}>👑 ELITE TIER:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px', color: '#374151' }}>
                    <li>Premium placement</li>
                    <li>All features</li>
                    <li>Dedicated support</li>
                  </ul>
                </div>
              </Box>
            </Box>
          )}
          
          {loading && <p>Loading shops...</p>}
          
          {error && (
            <Box sx={{
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              padding: '10px',
              borderRadius: '6px',
              marginBottom: '20px'
            }}>
              {error}
            </Box>
          )}
          
          {!loading && !error && shops.filter(shop => 
            shop.shop_name.toLowerCase().includes(nameFilter.toLowerCase())
          ).length === 0 && shops.length > 0 && (
            <p style={{ color: '#6b7280' }}>
              No shops found matching your filters: 
              {nameFilter && ` name contains "${nameFilter}"`}
              {statusFilter !== 'All' && ` status is ${statusFilter}`}
              {tierFilter !== 'All' && ` tier is ${tierFilter}`}.
            </p>
          )}
          
          {!loading && !error && shops.length === 0 && (
            <p style={{ color: '#6b7280' }}>
              No shops found.
            </p>
          )}
          
          {!loading && !error && shops.filter(shop => 
            shop.shop_name.toLowerCase().includes(nameFilter.toLowerCase())
          ).length > 0 && (
            <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh', border: '1px solid #e5e7eb' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', width: '18%', position: 'sticky', top: 0, backgroundColor: '#f9fafb', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>Shop Name</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', width: '10%', position: 'sticky', top: 0, backgroundColor: '#f9fafb', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>Phone</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', width: '20%', position: 'sticky', top: 0, backgroundColor: '#f9fafb', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>Website</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', width: '8%', position: 'sticky', top: 0, backgroundColor: '#f9fafb', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>Confidence</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', width: '22%', position: 'sticky', top: 0, backgroundColor: '#f9fafb', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', width: '22%', position: 'sticky', top: 0, backgroundColor: '#f9fafb', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>Tier</th>
                  </tr>
                </thead>
                <tbody>
                  {shops.filter(shop => 
                    shop.shop_name.toLowerCase().includes(nameFilter.toLowerCase())
                  ).map((shop) => (
                    <tr key={shop.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px' }}>
                        {shop.shop_name}
                        {shop.manually_entered && (
                          <Box
                            component="span"
                            sx={{
                              ml: 1,
                              px: 1,
                              py: 0.5,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: '#d97706',
                              backgroundColor: '#fef3c7',
                              border: '1px solid #fbbf24',
                              borderRadius: '4px',
                              display: 'inline-block'
                            }}
                            title="This shop was manually entered - verify no duplicates exist"
                          >
                            ✍️ MANUAL
                          </Box>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>{formatPhoneNumber(shop.phone_number)}</td>
                      <td style={{ padding: '12px', wordBreak: 'break-all', overflow: 'hidden' }}>
                        {shop.url ? (
                          <a 
                            href={shop.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={{ 
                              color: '#3b82f6',
                              textDecoration: 'none',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={shop.url}
                          >
                            {shop.url.length > 30 ? shop.url.substring(0, 30) + '...' : shop.url}
                          </a>
                        ) : 'N/A'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <ConfidenceScore 
                          score={shop.confidence_score || 0} 
                          onClick={() => handleShowConfidenceDetails(shop)}
                        />
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Button
                            onClick={() => handleStatusChange(shop.id, 'Active')}
                            disabled={shop.status === 'Active'}
                            size="small"
                            sx={{
                              backgroundColor: shop.status === 'Active' ? '#059669' : '#e5e7eb',
                              color: shop.status === 'Active' ? '#ffffff !important' : '#6b7280',
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              fontWeight: shop.status === 'Active' ? '600' : '400',
                              textTransform: 'none',
                              cursor: shop.status === 'Active' ? 'not-allowed' : 'pointer',
                              '&:hover': shop.status !== 'Active' ? {
                                backgroundColor: '#10b981',
                                color: 'white'
                              } : {}
                            }}
                          >
                            Active
                          </Button>
                          <Button
                            onClick={() => handleStatusChange(shop.id, 'Pending')}
                            disabled={shop.status === 'Pending'}
                            size="small"
                            sx={{
                              backgroundColor: shop.status === 'Pending' ? '#f59e0b' : '#e5e7eb',
                              color: shop.status === 'Pending' ? '#ffffff !important' : '#6b7280',
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              fontWeight: shop.status === 'Pending' ? '600' : '400',
                              textTransform: 'none',
                              cursor: shop.status === 'Pending' ? 'not-allowed' : 'pointer',
                              '&:hover': shop.status !== 'Pending' ? {
                                backgroundColor: '#f59e0b',
                                color: 'white'
                              } : {}
                            }}
                          >
                            Pending
                          </Button>
                          <Button
                            onClick={() => handleStatusChange(shop.id, 'NIN')}
                            disabled={shop.status === 'NIN'}
                            size="small"
                            sx={{
                              backgroundColor: shop.status === 'NIN' ? '#6b7280' : '#e5e7eb',
                              color: shop.status === 'NIN' ? '#ffffff !important' : '#6b7280',
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              fontWeight: shop.status === 'NIN' ? '600' : '400',
                              textTransform: 'none',
                              cursor: shop.status === 'NIN' ? 'not-allowed' : 'pointer',
                              '&:hover': shop.status !== 'NIN' ? {
                                backgroundColor: '#6b7280',
                                color: 'white'
                              } : {}
                            }}
                          >
                            NIN
                          </Button>
                          <Button
                            onClick={() => handleStatusChange(shop.id, 'Inactive')}
                            disabled={shop.status === 'Inactive'}
                            size="small"
                            sx={{
                              backgroundColor: shop.status === 'Inactive' ? '#dc2626' : '#e5e7eb',
                              color: shop.status === 'Inactive' ? '#ffffff !important' : '#6b7280',
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              fontWeight: shop.status === 'Inactive' ? '600' : '400',
                              textTransform: 'none',
                              cursor: shop.status === 'Inactive' ? 'not-allowed' : 'pointer',
                              '&:hover': shop.status !== 'Inactive' ? {
                                backgroundColor: '#ef4444',
                                color: 'white'
                              } : {}
                            }}
                          >
                            Inactive
                          </Button>
                          <Button
                            onClick={() => handleRejectClick(shop)}
                            disabled={shop.status === 'Suspended'}
                            size="small"
                            sx={{
                              backgroundColor: shop.status === 'Suspended' ? '#7f1d1d' : '#e5e7eb',
                              color: shop.status === 'Suspended' ? '#ffffff !important' : '#6b7280',
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              fontWeight: shop.status === 'Suspended' ? '600' : '400',
                              textTransform: 'none',
                              cursor: shop.status === 'Suspended' ? 'not-allowed' : 'pointer',
                              '&:hover': shop.status !== 'Suspended' ? {
                                backgroundColor: '#991b1b',
                                color: 'white'
                              } : {}
                            }}
                          >
                            Reject
                          </Button>
                        </Box>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Button
                            onClick={() => handleTierChange(shop.id, 'Free')}
                            disabled={shop.saas_model === 'Free'}
                            size="small"
                            sx={{
                              backgroundColor: shop.saas_model === 'Free' ? '#10b981' : '#e5e7eb',
                              color: shop.saas_model === 'Free' ? '#ffffff !important' : '#6b7280',
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              fontWeight: shop.saas_model === 'Free' ? '600' : '400',
                              textTransform: 'none',
                              cursor: shop.saas_model === 'Free' ? 'not-allowed' : 'pointer',
                              '&:hover': shop.saas_model !== 'Free' ? {
                                backgroundColor: '#10b981',
                                color: 'white'
                              } : {}
                            }}
                          >
                            Free
                          </Button>
                          <Button
                            onClick={() => handleTierChange(shop.id, 'Pro')}
                            disabled={shop.saas_model === 'Pro'}
                            size="small"
                            sx={{
                              backgroundColor: shop.saas_model === 'Pro' ? '#3b82f6' : '#e5e7eb',
                              color: shop.saas_model === 'Pro' ? '#ffffff !important' : '#6b7280',
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              fontWeight: shop.saas_model === 'Pro' ? '600' : '400',
                              textTransform: 'none',
                              cursor: shop.saas_model === 'Pro' ? 'not-allowed' : 'pointer',
                              '&:hover': shop.saas_model !== 'Pro' ? {
                                backgroundColor: '#3b82f6',
                                color: 'white'
                              } : {}
                            }}
                          >
                            Pro
                          </Button>
                          <Button
                            onClick={() => handleTierChange(shop.id, 'Elite')}
                            disabled={shop.saas_model === 'Elite'}
                            size="small"
                            sx={{
                              backgroundColor: shop.saas_model === 'Elite' ? '#8b5cf6' : '#e5e7eb',
                              color: shop.saas_model === 'Elite' ? '#ffffff !important' : '#6b7280',
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              fontWeight: shop.saas_model === 'Elite' ? '600' : '400',
                              textTransform: 'none',
                              cursor: shop.saas_model === 'Elite' ? 'not-allowed' : 'pointer',
                              '&:hover': shop.saas_model !== 'Elite' ? {
                                backgroundColor: '#8b5cf6',
                                color: 'white'
                              } : {}
                            }}
                          >
                            Elite
                          </Button>
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Box>
      </Box>
      
      {/* Confidence Score Details Modal */}
      {selectedShopForConfidence && (
        <ConfidenceScoreDetails 
          shopId={selectedShopForConfidence.id}
          onClose={() => setSelectedShopForConfidence(null)}
          onScoreUpdated={(shopId, newScore) => {
            // Update the score in the local state
            setShops(prevShops => 
              prevShops.map(shop => 
                shop.id === shopId 
                  ? { ...shop, confidence_score: newScore }
                  : shop
              )
            );
          }}
        />
      )}

      {/* Rejection Modal */}
      <Dialog 
        open={rejectModalOpen} 
        onClose={() => setRejectModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#dc2626', 
          color: 'white',
          fontWeight: 600,
          fontSize: '1.25rem'
        }}>
          ⚠️ Reject Shop Application
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedShopForReject && (
            <>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                Shop: {selectedShopForReject.shop_name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
                The rejection reason below has been auto-generated based on the shop's current data. 
                You can edit or completely rewrite it before sending.
              </Typography>
              <TextField
                label="Rejection Reason (Required)"
                multiline
                rows={8}
                fullWidth
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                required
                error={!rejectionReason.trim()}
                helperText={!rejectionReason.trim() ? "Rejection reason is required" : "This message will be shown to the shop owner"}
                sx={{ mb: 2 }}
              />
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#fef3c7', 
                borderRadius: 1,
                border: '1px solid #fbbf24'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400e', mb: 1 }}>
                  💡 Quick Suggestions:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {selectedShopForReject.confidence < 50 && (
                    <Typography variant="body2" sx={{ color: '#92400e' }}>
                      • Confidence score: {selectedShopForReject.confidence}% (need 50%+)
                    </Typography>
                  )}
                  {!selectedShopForReject.business_license_url && (
                    <Typography variant="body2" sx={{ color: '#92400e' }}>
                      • Missing business license
                    </Typography>
                  )}
                  {!selectedShopForReject.storefront_photo_url && (
                    <Typography variant="body2" sx={{ color: '#92400e' }}>
                      • Missing storefront photo
                    </Typography>
                  )}
                  {selectedShopForReject.confidence >= 50 && 
                   selectedShopForReject.business_license_url && 
                   selectedShopForReject.storefront_photo_url && (
                    <Typography variant="body2" sx={{ color: '#92400e' }}>
                      ✅ All requirements met - consider manual review reason
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setRejectModalOpen(false);
              setSelectedShopForReject(null);
              setRejectionReason('');
            }}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReject}
            disabled={!rejectionReason.trim()}
            variant="contained"
            sx={{
              backgroundColor: '#dc2626',
              '&:hover': {
                backgroundColor: '#991b1b'
              },
              '&:disabled': {
                backgroundColor: '#e5e7eb',
                color: '#9ca3af'
              }
            }}
          >
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminDashboard;